/**
 * Skrip pasang agen telemetri (ADR-0055 Fase 3) — MURNI, teruji di Node.
 *
 * Menghasilkan skrip siap-jalan yang MENANAM token di dalamnya supaya teknisi tak perlu mengetik
 * env sama sekali: terbitkan token di app → unduh skrip → jalankan. Skrip memasang agen sebagai
 * LAYANAN (startup), mencerminkan `docs/operations/workforce-agent-enrollment-runbook.md` §3.3
 * (systemd untuk Linux; Scheduled Task untuk Windows) dan kontrak agen (`WFA_API_URL`/`WFA_TOKEN`,
 * biner dijalankan langsung).
 *
 * `distBaseUrl` opsional: bila diisi (URL rilis publik biner, lihat workforce-agent-release-setup),
 * skrip AUTO-MENGUNDUH biner bila belum ada di folder — jadi teknisi cukup unduh skrip + jalankan.
 * Bila kosong, skrip mengharapkan biner sudah ada di folder yang sama (perilaku sebelumnya).
 *
 * Token mentah ADA di dalam skrip — konsekuensi "sekali-klik". Skrip menaruh env di cakupan
 * mesin/berkas ber-izin ketat (bukan di baris perintah), dan memuat peringatan agar berkas skrip
 * tidak dibagikan. Nilai Windows dibungkus kutip & di-escape; nilai Linux ditulis di heredoc
 * BERKUTIP (`<<'ENV'`) sehingga tak diekspansi apa pun.
 */
export type AgenOS = 'windows' | 'linux';

export const ASET_BINER: Record<AgenOS, string> = {
  windows: 'workforce-agent-windows-amd64.exe',
  linux: 'workforce-agent-linux-amd64',
};

/** Nama berkas unduhan per OS. */
export function namaSkrip(os: AgenOS): string {
  return os === 'windows' ? 'pasang-agen-workforce.ps1' : 'pasang-agen-workforce.sh';
}

/**
 * Escape untuk string ber-kutip-ganda PowerShell. Backtick dulu (karakter escape PS), lalu `$`
 * (mencegah interpolasi `$var`/`$(...)` → `` `$ ``) dan `"` (→ `` `" ``). Urutan penting: backtick
 * di-escape lebih dulu agar backtick yang kita tambahkan tidak ikut ter-escape lagi.
 */
function escPwsh(s: string): string {
  return s.replace(/`/g, '``').replace(/\$/g, '`$').replace(/"/g, '`"');
}

/**
 * Skrip Windows PowerShell. Menaruh `WFA_API_URL`/`WFA_TOKEN` di env MESIN (tak tampak di daftar
 * proses) lalu mendaftarkan Scheduled Task "WorkforceAgent" yang jalan saat startup dengan hak
 * tertinggi. Biner diharapkan di folder skrip; bila `distBaseUrl` diisi dan biner belum ada, skrip
 * mengunduhnya lebih dulu.
 */
export function skripPasangWindows(apiUrl: string, token: string, distBaseUrl = ''): string {
  const u = escPwsh(apiUrl);
  const t = escPwsh(token);
  const blokBiner = distBaseUrl
    ? `  Write-Host "Mengunduh biner agen..."
  Invoke-WebRequest -Uri "${escPwsh(distBaseUrl)}/${ASET_BINER.windows}" -OutFile $Exe`
    : `  Write-Error "Biner tidak ditemukan: $Exe — taruh ${ASET_BINER.windows} di folder yang sama dengan skrip ini."
  exit 1`;
  return `# Pasang Agen Telemetri Workforce (Windows) — token sudah tertanam.
# JANGAN bagikan berkas ini: memuat token rahasia alat ini.
# Jalankan: klik kanan > "Run with PowerShell" (sebagai Administrator), atau:
#   powershell -ExecutionPolicy Bypass -File .\\pasang-agen-workforce.ps1
#Requires -RunAsAdministrator
$ErrorActionPreference = "Stop"
$ApiUrl = "${u}"
$Token  = "${t}"
$Dir    = $PSScriptRoot
$Exe    = Join-Path $Dir "${ASET_BINER.windows}"

if (-not (Test-Path $Exe)) {
${blokBiner}
}

# Env tingkat MESIN (tak tampak di daftar proses, tak hilang saat reboot).
[Environment]::SetEnvironmentVariable("WFA_API_URL", $ApiUrl, "Machine")
[Environment]::SetEnvironmentVariable("WFA_TOKEN", $Token, "Machine")

# Scheduled Task: jalan saat startup, hak tertinggi, walau belum login.
$Action    = New-ScheduledTaskAction -Execute $Exe
$Trigger   = New-ScheduledTaskTrigger -AtStartup
$Principal = New-ScheduledTaskPrincipal -UserId "SYSTEM" -LogonType ServiceAccount -RunLevel Highest
$Settings  = New-ScheduledTaskSettingsSet -RestartCount 999 -RestartInterval (New-TimeSpan -Minutes 1) -StartWhenAvailable
Register-ScheduledTask -TaskName "WorkforceAgent" -Action $Action -Trigger $Trigger -Principal $Principal -Settings $Settings -Force | Out-Null

# Jalankan sekarang juga agar tak perlu menunggu reboot.
Start-ScheduledTask -TaskName "WorkforceAgent"
Write-Host "Agen terpasang & berjalan. Cek di aplikasi Workforce: kartu alat jadi 'Agen aktif' < 1 menit."
`;
}

/**
 * Skrip Linux (systemd). Menyalin biner ke /opt, menulis env file ber-izin 600, memasang unit
 * (sama persis runbook §3.3), lalu enable --now. Perlu root. Biner diharapkan di folder skrip
 * (nama `workforce-agent` atau `workforce-agent-linux-amd64`); bila `distBaseUrl` diisi dan tak
 * ada, skrip mengunduhnya lebih dulu.
 */
export function skripPasangLinux(apiUrl: string, token: string, distBaseUrl = ''): string {
  const blokBiner = distBaseUrl
    ? `if [ -z "$SRC" ]; then
  echo "Mengunduh biner agen..."
  curl -fsSL '${distBaseUrl.replace(/'/g, `'\\''`)}/${ASET_BINER.linux}' -o "$DIR/workforce-agent"
  chmod +x "$DIR/workforce-agent"
  SRC="$DIR/workforce-agent"
fi`
    : `if [ -z "$SRC" ]; then
  echo "Biner tidak ditemukan di $DIR (cari: workforce-agent / workforce-agent-linux-amd64)."; exit 1
fi`;
  return `#!/usr/bin/env bash
# Pasang Agen Telemetri Workforce (Linux/systemd) — token sudah tertanam.
# JANGAN bagikan berkas ini: memuat token rahasia alat ini.
# Jalankan: sudo bash pasang-agen-workforce.sh
set -euo pipefail

if [ "$(id -u)" -ne 0 ]; then echo "Jalankan sebagai root: sudo bash $0"; exit 1; fi

DIR="$(cd "$(dirname "$0")" && pwd)"
SRC=""
for n in workforce-agent workforce-agent-linux-amd64; do
  if [ -x "$DIR/$n" ] || [ -f "$DIR/$n" ]; then SRC="$DIR/$n"; break; fi
done
${blokBiner}

install -D -m 0755 "$SRC" /opt/workforce-agent/workforce-agent

umask 077
# Heredoc BERKUTIP (<<'ENV'): bash tak mengekspansi apa pun di isinya, jadi karakter spesial
# (misalnya tanda dolar atau backtick) tak mungkin dieksekusi — keamanan tak bergantung pada
# escaping per-karakter maupun pada charset token. systemd EnvironmentFile membaca value apa adanya.
cat > /etc/workforce-agent.env <<'ENV'
WFA_API_URL=${apiUrl}
WFA_TOKEN=${token}
ENV
chmod 600 /etc/workforce-agent.env

cat > /etc/systemd/system/workforce-agent.service <<'UNIT'
[Unit]
Description=HBS Workforce Telemetry Agent
After=network-online.target
Wants=network-online.target

[Service]
EnvironmentFile=/etc/workforce-agent.env
ExecStart=/opt/workforce-agent/workforce-agent
Restart=always
RestartSec=10
DynamicUser=yes
NoNewPrivileges=yes
ProtectSystem=strict
ProtectHome=yes

[Install]
WantedBy=multi-user.target
UNIT

systemctl daemon-reload
systemctl enable --now workforce-agent
echo "Agen terpasang & berjalan. Cek di aplikasi Workforce: kartu alat jadi 'Agen aktif' < 1 menit."
echo "Log: journalctl -u workforce-agent -f"
`;
}

/** Skrip pasang untuk OS tertentu dengan token tertanam (+ auto-unduh biner bila distBaseUrl diisi). */
export function skripPasang(apiUrl: string, token: string, os: AgenOS, distBaseUrl = ''): string {
  return os === 'windows'
    ? skripPasangWindows(apiUrl, token, distBaseUrl)
    : skripPasangLinux(apiUrl, token, distBaseUrl);
}
